import React, { FC, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Box } from "@UI/Components/Box/Box";
import { Flex } from "@UI/Components/Flex/Flex";
import { Text } from "@UI/Components/Text/Text";
import { Input } from "@UI/Components/Input/Input";
import { IconButton } from "@UI/Components/Button/Button";
import { Plus } from "@UI/Components/Icons/Plus";
import { Minus } from "@UI/Components/Icons/Minus";
import { CircleInfo } from "@UI/Components/Icons/CircleInfo";
import messages from "./messages";

export interface DurationInputProps {
  /** Total minutes. `null` when the field is empty or not a valid HH:MM. */
  valueInMinutes: number | null;
  onChange: (valueInMinutes: number | null) => void;
  /** Smallest duration (in minutes) the − button and a blur snap will go down to. */
  min?: number;
  /** Minutes added/removed by the buttons; typed values snap to it on blur. */
  step?: number;
  isInvalid?: boolean;
  errorMessage?: string;
  isDisabled?: boolean;
}

const MAX_DIGITS = 4;
const MAX_MINUTES = 99 * 60 + 59;

const stepperButtonProps = {
  isRound: true,
  bg: "gray.300",
  boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.10)",
};

const pad = (value: number) => String(value).padStart(2, "0");

export const formatDuration = (minutes: number | null): string =>
  minutes === null ? "" : `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;

/** Digits only, colon inserted before the last two: "1145" → "11:45", "145" → "1:45". */
export const maskDuration = (raw: string): string => {
  const digits = raw.replace(/\D/g, "").slice(0, MAX_DIGITS);
  return digits.length > 2 ? `${digits.slice(0, -2)}:${digits.slice(-2)}` : digits;
};

/** `null` for an empty or half-typed value, or minutes above 59. */
export const parseDuration = (masked: string): number | null => {
  const digits = masked.replace(/\D/g, "");
  if (digits.length < 3) return null;
  const hours = Number(digits.slice(0, -2));
  const minutes = Number(digits.slice(-2));
  return minutes > 59 ? null : hours * 60 + minutes;
};

export const DurationInput: FC<DurationInputProps> = ({
  valueInMinutes,
  onChange,
  min = 0,
  step = 15,
  isInvalid = false,
  errorMessage = "",
  isDisabled = false,
}) => {
  const intl = useIntl();
  const [text, setText] = useState(() => formatDuration(valueInMinutes));

  // Follow the value when it changes from outside (buttons, a legacy reset).
  // While the typed text still parses to it, leave the text alone so a
  // half-typed "1:4" isn't rewritten under the cursor.
  useEffect(() => {
    setText((current) =>
      parseDuration(current) === valueInMinutes ? current : formatDuration(valueInMinutes)
    );
  }, [valueInMinutes]);

  const handleTyping = (event: React.ChangeEvent<HTMLInputElement>) => {
    const masked = maskDuration(event.target.value);
    setText(masked);
    onChange(parseDuration(masked));
  };

  const handleBlur = () => {
    const typed = parseDuration(text);
    if (typed === null) return;
    const snapped = Math.round(typed / step) * step;
    setText(formatDuration(snapped));
    onChange(snapped);
  };

  const change = (next: number) => {
    setText(formatDuration(next));
    onChange(next);
  };

  const canDecrease = valueInMinutes !== null && valueInMinutes > min;

  return (
    <Box>
      <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" padding={4} bg="white">
        <Text
          color="gray.800"
          fontSize="sm"
          fontWeight="700"
          lineHeight="150%"
          textAlign="center"
          mb={3}
        >
          {intl.formatMessage(messages.caption)}
        </Text>
        <Flex align="center" justify="center" gap={4} alignSelf="stretch">
          <IconButton
            aria-label={intl.formatMessage(messages.decrease)}
            icon={<Minus />}
            isDisabled={isDisabled || !canDecrease}
            onClick={() => change(Math.max((valueInMinutes ?? 0) - step, min))}
            {...stepperButtonProps}
          />
          <Input
            inputMode="numeric"
            textAlign="center"
            color="gray.800"
            fontFamily="heading"
            fontSize="4xl"
            fontWeight="700"
            lineHeight="120%"
            width="160px"
            value={text}
            isInvalid={isInvalid}
            isDisabled={isDisabled}
            onChange={handleTyping}
            onBlur={handleBlur}
            placeholder="00:00"
            _placeholder={{ color: "gray.400" }}
            sx={{ "&:focus::placeholder": { color: "transparent" } }}
          />
          <IconButton
            aria-label={intl.formatMessage(messages.increase)}
            icon={<Plus />}
            isDisabled={isDisabled}
            onClick={() => change(Math.min(Math.max((valueInMinutes ?? 0) + step, min), MAX_MINUTES))}
            {...stepperButtonProps}
          />
        </Flex>
      </Box>
      {isInvalid && errorMessage && (
        <Flex align="center" justify="center" gap={2} mt={2} color="red.500" role="alert">
          <CircleInfo />
          <Text color="red.500" fontSize="md" lineHeight="150%">
            {errorMessage}
          </Text>
        </Flex>
      )}
    </Box>
  );
};
